import { useEffect, useState } from 'react';

export interface EvieOrbProps {
  /** Render size in px. The asset downscales from its intrinsic size. */
  size?: number;
  /** Extra class passed through to the underlying <img>. */
  className?: string;
  /** Accessible label / alt text. */
  title?: string;
}

/**
 * The EVII assistant orb for the ADMIN app -- a local replica of the
 * driver-app EvieOrb (apps/web/src/components/primitives/EvieOrb.tsx).
 * The two apps are separate Vite builds and don't share component code,
 * so the orb is replicated here and reads the admin app's own copies of
 * the brand WebP assets in apps/admin/public/.
 *
 * Rendered as an animated WebP <img> -- it loops natively over a
 * transparent background with a small payload and no <video> autoplay
 * quirks. Deliberately NOT a Lottie slot: the orb is a brand
 * raster/animated WebP, not vector JSON.
 *
 * Honours `prefers-reduced-motion: reduce` by swapping in the static
 * poster frame, so users who opt out of motion still see the orb but
 * don't get the loop.
 *
 * The two assets live in apps/admin/public/ and serve at the root:
 *   - /evie-orb.webp         animated, loops natively
 *   - /evie-orb-poster.webp  static first frame (reduced-motion + poster)
 */
export function EvieOrb({ size = 24, className, title = 'EVII' }: EvieOrbProps) {
  // Default to "no preference" so first paint renders the animation.
  // The effect flips us to the static poster only when the user has the
  // reduce preference, and listens for live changes.
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return (
    <img
      src={reducedMotion ? '/evie-orb-poster.webp' : '/evie-orb.webp'}
      alt={title}
      width={size}
      height={size}
      draggable={false}
      className={className}
      // Intrinsic dimensions + explicit size prevent CLS on first paint.
      style={{
        display: 'block',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    />
  );
}
